import axios from "axios";
import { authService } from "./authService";
import store from "../app/store";
import { startLoading, stopLoading } from "../features/ui/uiSlice";
import { savePostLoginRedirect } from "./postLoginRedirect";
import json from "../secret.json";

const API_BASE_URL = json.API_BASE_URL;

const instance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Refresh 요청용 인스턴스 (Interceptor 돌기 전 순수 인스턴스)
const refreshInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// 진행 중인 토큰 갱신. 여러 요청이 동시에 401 을 받아도 갱신은 한 번만 나간다.
//
// 리프레시 토큰은 한 번 쓰면 폐기된다(서버의 SIMPLE_JWT 가 ROTATE_REFRESH_TOKENS +
// BLACKLIST_AFTER_ROTATION). 그래서 요청마다 각자 갱신을 부르면 첫 하나만 성공하고
// 나머지는 401 을 받는다. 아래 로직이 그것을 '로그인 만료' 로 보고 토큰을 지우며
// 로그인 화면으로 보내므로, 진행 중이던 작업이 통째로 사라진다. 학생 여러 명을
// 한꺼번에 지웠을 때 한 명도 안 지워진 것이 이 때문이었다.
let refreshPromise = null;

function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = authService.getRefreshToken();
  if (!refreshToken) {
    const error = new Error("리프레시 토큰이 없습니다.");
    error.isSessionExpired = true;
    return Promise.reject(error);
  }

  refreshPromise = refreshInstance
    .post("/api/v1/token/refresh", { refresh: refreshToken })
    .then((response) => {
      const { access, refresh } = response.data;
      authService.setTokens(access, refresh);
      return access;
    })
    .finally(() => {
      // 다음 만료 때는 새 리프레시 토큰으로 다시 한 번 갱신할 수 있어야 한다.
      refreshPromise = null;
    });

  return refreshPromise;
}

// 갱신 실패를 '다시 로그인해야 하는 만료' 와 '잠깐의 장애' 로 가른다.
//
// 서버가 잠시 죽거나 네트워크가 끊긴 것까지 만료로 보면, 멀쩡한 리프레시 토큰을
// 지우고 로그인으로 튕겨 보내 하던 작업이 통째로 사라진다. 되돌릴 수 없는 것은
// 리프레시 토큰이 없거나(위) 서버가 그 토큰을 거절한 경우(401)뿐이고, 응답 자체가
// 없거나 5xx 면 토큰을 그대로 두고 원래 요청만 실패시킨다.
function isSessionExpiredError(error) {
  return error?.isSessionExpired === true || error?.response?.status === 401;
}

// 돌아올 곳으로 기억하면 안 되는 주소. 로그인 흐름 자체의 화면들이라, 여기를
// 목적지로 저장하면 다시 로그인한 뒤 곧장 로그인 화면으로 되돌아가 무한히 돈다.
// (`/login` 에서도 만료가 날 수 있다. 죽은 토큰이 남아 있으면 App 이 계정 정보를
// 받아 보다가 401 을 맞는다.)
const NO_RETURN_PATHS = ["/login", "/callback"];

// 로그인 만료 처리가 요청 수만큼 돌지 않게 한 번만 한다.
let isSessionExpiredHandled = false;

function handleSessionExpired() {
  if (isSessionExpiredHandled) {
    return;
  }
  isSessionExpiredHandled = true;

  authService.removeTokens();
  // 보던 화면을 기억해 두고 조용히 다시 로그인시킨다. 지인에듀 세션이 살아 있으면
  // OAuth 가 동의 화면 없이 통과하므로 사용자는 깜빡임 한 번으로 제자리에 돌아온다.
  // 사용자가 할 일이 없는 흐름이라 안내를 띄우지 않는다. 예전에는 alert 로 막아
  // 세워 두었는데, 확인을 누르기 전까지 이동조차 하지 않았다.
  if (!NO_RETURN_PATHS.includes(window.location.pathname)) {
    savePostLoginRedirect(window.location.pathname + window.location.search);
  }
  window.location.href = "/login";
}

// 요청 인터셉터
instance.interceptors.request.use(
  (config) => {
    // withAuth 옵션이 true인 경우에만 Authorization 헤더에 토큰 주입
    if (config.withAuth) {
      const token = authService.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    store.dispatch(startLoading());
    return config;
  },
  (error) => {
    store.dispatch(stopLoading());

    return Promise.reject(error);
  }
);

// 응답 인터셉터
instance.interceptors.response.use(
  (response) => {
    store.dispatch(stopLoading());
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 401 발생 시 Refresh Token 로직
    if (
      error.response &&
      error.response.status === 401 &&
      originalRequest.withAuth &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const access = await refreshAccessToken();

        originalRequest.headers["Authorization"] = `Bearer ${access}`;
        const finalResponse = await instance(originalRequest);
        store.dispatch(stopLoading());

        return finalResponse;
      } catch (refreshError) {
        store.dispatch(stopLoading());
        if (isSessionExpiredError(refreshError)) {
          handleSessionExpired();
        }

        return Promise.reject(refreshError);
      }
    }
    store.dispatch(stopLoading());
    return Promise.reject(error);
  }
);

const apiClient = {
  async get(endpoint, config = {}) {
    const response = await instance.get(endpoint, config);
    return response;
  },

  async post(endpoint, data, config = {}) {
    const response = await instance.post(endpoint, data, config);
    return response;
  },

  async put(endpoint, data, config = {}) {
    const response = await instance.put(endpoint, data, config);
    return response;
  },

  async patch(endpoint, data, config = {}) {
    const response = await instance.patch(endpoint, data, config);
    return response;
  },

  async delete(endpoint, config = {}) {
    const response = await instance.delete(endpoint, config);
    return response;
  },
};

export default apiClient;
